import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import GalleryViewer from '../components/GalleryViewer'
import ContactFormSection from '../components/ContactFormSection'

export default function Porebeton() {
  useEffect(() => {
    document.title = 'Porebeton - Kristensen Byg og Montage ApS'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Professionelt arbejde med porebeton og gasbeton. Vi opbygger solide indervægge, bagmure og skillevægge med høj præcision og fokus på det gode håndværk.'
      )
    }
  }, [])

  const getResizedUrl = (url, width, quality = 80) => {
    if (!url || !url.startsWith('https://assets.auxodev.dk/')) return url
    return url.replace(
      'https://assets.auxodev.dk/',
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/`
    )
  }

  const mainImage = 'https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/20260701-165032.jpg'

  return (
    <div className="bg-white min-h-screen">
      {/* Service Details */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column: Content */}
          <div className="space-y-6">
            <h1 className="text-3xl sm:text-4xl font-extrabold uppercase tracking-wider text-anthracite leading-tight">
              Professionelt arbejde med porebeton og gasbeton
            </h1>
            <p className="text-base sm:text-lg text-warm-gray-700 leading-relaxed font-medium">
              Opbygning af solide indervægge, bagmure og skillevægge med millimeterpræcision og høj faglig stolthed.
            </p>
            <p className="text-sm sm:text-base text-warm-gray-600 leading-relaxed">
              Arbejde med porebeton (også kendt som gasbeton eller multiplader) kræver stor nøjagtighed, den rette limteknik og grundig planlægning. Hos Kristensen Byg og Montage ApS udfører jeg professionel opsætning af porebetonelementer til både nybyggeri, tilbygninger og renoveringsprojekter.
            </p>
            <p className="text-sm sm:text-base text-warm-gray-600 leading-relaxed">
              Porebeton er et fantastisk materiale med fremragende isoleringsegenskaber, høj brandsikkerhed, god lyddæmpning og stor fleksibilitet. Jeg sikrer, at alle blokke og elementer sættes med millimeterpræcision, så du får snorlige, stærke vægge, der er lige til at gå til for maleren.
            </p>

            {/* Key highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-check text-brand-green mt-1 text-base" />
                <span className="text-sm font-semibold text-warm-gray-800">Opsætning af skillevægge</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-check text-brand-green mt-1 text-base" />
                <span className="text-sm font-semibold text-warm-gray-800">Opbygning af bagmure</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-check text-brand-green mt-1 text-base" />
                <span className="text-sm font-semibold text-warm-gray-800">Millimeterpræcision</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-check text-brand-green mt-1 text-base" />
                <span className="text-sm font-semibold text-warm-gray-800">Høj isoleringsevne</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-check text-brand-green mt-1 text-base" />
                <span className="text-sm font-semibold text-warm-gray-800">Solide og stærke vægge</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-check text-brand-green mt-1 text-base" />
                <span className="text-sm font-semibold text-warm-gray-800">Professionelt udstyr</span>
              </div>
            </div>

            <div className="pt-6 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center rounded bg-brand-green px-6 py-3.5 text-sm font-bold text-white shadow-sm hover:bg-brand-green-hover transition-colors duration-200 uppercase tracking-wider"
              >
                Få et tilbud på porebeton
              </Link>
            </div>
          </div>

          {/* Right Column: Image */}
          <div className="relative aspect-4/3 overflow-hidden border border-warm-gray-200 shadow-xl rounded-lg">
            <img
              src={getResizedUrl(mainImage, 1000, 85)}
              alt="Professionelt opsat porebeton på byggeplads"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Process / Advantages Grid Section */}
      <div className="bg-warm-gray-50 border-y border-warm-gray-200 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase tracking-wider text-anthracite mb-4">
              Hvorfor vælge porebeton til dit byggeri?
            </h2>
            <p className="text-sm sm:text-base text-warm-gray-600 leading-relaxed">
              Porebeton kombinerer rå styrke med let forarbejdning og giver et formidabelt indeklima samt lige vægge.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 border border-warm-gray-200 rounded-lg shadow-sm flex flex-col justify-between">
              <div>
                <div className="h-12 w-12 rounded bg-brand-green/10 text-brand-green flex items-center justify-center text-xl font-bold mb-6">
                  <i className="fa-light fa-ruler-combined" />
                </div>
                <h3 className="text-lg font-bold uppercase tracking-wider text-anthracite mb-3">
                  Snorlige resultater
                </h3>
                <p className="text-xs sm:text-sm text-warm-gray-600 leading-relaxed">
                  Med præcis limteknik og nøje opmåling står dine nye indervægge snorlige og klar til viderebehandling uden unødige ujævnheder.
                </p>
              </div>
              <div className="mt-6 pt-6 border-t border-warm-gray-100 overflow-hidden rounded">
                <img
                  src={getResizedUrl("https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/20260618-123603.jpg", 500, 80)}
                  alt="Snorlige porebeton skillevæg"
                  className="w-full h-36 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>

            <div className="bg-white p-8 border border-warm-gray-200 rounded-lg shadow-sm flex flex-col justify-between">
              <div>
                <div className="h-12 w-12 rounded bg-brand-green/10 text-brand-green flex items-center justify-center text-xl font-bold mb-6">
                  <i className="fa-light fa-temperature-arrow-up" />
                </div>
                <h3 className="text-lg font-bold uppercase tracking-wider text-anthracite mb-3">
                  Optimal isolering
                </h3>
                <p className="text-xs sm:text-sm text-warm-gray-600 leading-relaxed">
                  Porebetonens lukkede luftceller giver fremragende varmeisolering og lyddæmpning, hvilket skaber et behageligt indeklima i rummet.
                </p>
              </div>
              <div className="mt-6 pt-6 border-t border-warm-gray-100 overflow-hidden rounded">
                <img
                  src={getResizedUrl("https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/20260529-164139.jpg", 500, 80)}
                  alt="Opbygning af porebeton råhus"
                  className="w-full h-36 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>

            <div className="bg-white p-8 border border-warm-gray-200 rounded-lg shadow-sm flex flex-col justify-between">
              <div>
                <div className="h-12 w-12 rounded bg-brand-green/10 text-brand-green flex items-center justify-center text-xl font-bold mb-6">
                  <i className="fa-light fa-cubes" />
                </div>
                <h3 className="text-lg font-bold uppercase tracking-wider text-anthracite mb-3">
                  Hurtig fremdrift
                </h3>
                <p className="text-xs sm:text-sm text-warm-gray-600 leading-relaxed">
                  Porebetonelementer og -blokke muliggør hurtig og effektiv montage, så byggeprocessen skrider stabilt og sikkert frem.
                </p>
              </div>
              <div className="mt-6 pt-6 border-t border-warm-gray-100 overflow-hidden rounded">
                <img
                  src={getResizedUrl("https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/20201005-073811.jpg", 500, 80)}
                  alt="Arbejde på porebeton væg"
                  className="w-full h-36 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Section */}
      <div className="bg-warm-gray-50 border-t border-b border-warm-gray-200 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold uppercase tracking-wider text-anthracite">
              Udvalgte projekter
            </h3>
          </div>

          {/* CMS Gallery Viewer */}
          <GalleryViewer slug="porebeton" />
        </div>
      </div>

      {/* Contact Form Section */}
      <ContactFormSection
        title="Skal du have opført vægge i porebeton?"
        subtitle="Uanset om det drejer sig om en enkelt skillevæg i boligen eller opbygning af vægge i et helt nybyggeri, står jeg klar med professionel ekspertise. Kontakt mig for et uforpligtende tilbud."
      />
    </div>
  )
}
